import React from "react";
import { SHOW_MERCHANT_PLANS, SHOW_PRICING } from "../siteConfig";

/** Entries that exist only to publish the fare schedule. */
const PRICING_FAQ_IDS = ["fares-and-fees", "ride-fares", "customer-booking-fee"];

/**
 * FAQ content for /faq.
 *
 * Answers are authored as JSX (`answer`) for display and mirrored as plain
 * text (`answerText`) for search matching and FAQPage JSON-LD. Both must be
 * kept in sync when an entry changes.
 *
 * Built as a factory because several answers link into the web app, whose URL
 * comes from the environment at runtime.
 */

export type FaqCategory =
  | "Getting started"
  | "Bookings & services"
  | "Payments & fees"
  | "For merchants"
  | "Security & privacy"
  | "Support";

export type FaqItem = {
  id: string;
  category: FaqCategory;
  question: string;
  answerText: string;
  answer: React.ReactNode;
  keywords?: string[];
};

export const buildFaqs = (webappUrl: string, signupUrl: string): FaqItem[] =>
  allFaqs(webappUrl, signupUrl).filter(
    (faq) =>
      (SHOW_PRICING || !PRICING_FAQ_IDS.includes(faq.id)) &&
      (SHOW_MERCHANT_PLANS || faq.category !== "For merchants"),
  );

const allFaqs = (webappUrl: string, signupUrl: string): FaqItem[] => [
      {
        id: "what-is-pabs",
        category: "Getting started",
        question: "What is PABS?",
        answerText:
          "PABS is an all-in-one app with four services: Foods (meals and drinks), Pabili (groceries, pharmacy items, and other shopping), Pasugo errands, and Angkas passenger rides. PABS: Pickup And Bring Stuff.",
        answer: (
          <>
            <p>PABS is an all-in-one app with four services:</p>
            <ul>
              <li><strong>Foods</strong> — meals and drinks from local restaurants and cafés.</li>
              <li><strong>Pabili</strong> — groceries, pharmacy items, and other shopping from nearby stores.</li>
              <li><strong>Pasugo</strong> — errands: tell a rider what to buy or what to bring where.</li>
              <li>
                <strong>Angkas</strong> — a rider takes you where you need to
                go. See <a className="faq-link" href="#what-is-angkas">how Angkas works</a>.
              </li>
            </ul>
            <p>
              <strong>PABS: Pickup And Bring Stuff.</strong> Every booking
              shows the fare before you book, with live status updates until
              it's done.
            </p>
          </>
        ),
        keywords: ["delivery", "food", "foods", "pabili", "grocery", "pharmacy", "pasugo", "errands", "ride", "angkas", "ride share", "pickup and bring stuff"],
      },
      {
        id: "how-to-register",
        category: "Getting started",
        question: "How do I sign up?",
        answerText:
          "Download the PABS app and sign in with Google, Apple, or your phone number. Your account is created the first time you sign in. You can also use PABS on the web at https://orders.pabs.online.",
        answer: (
          <>
            <p>
              <a className="faq-link" href="/download">Download the PABS app</a>{" "}
              and choose <strong>Continue with Google</strong>,{" "}
              <strong>Continue with Apple</strong>, or{" "}
              <strong>Use phone number</strong> (we text you a 6-digit code).
              Your account is created the first time you sign in.
            </p>
            <p>
              You can also use PABS in your browser at{" "}
              <a className="faq-link" href={signupUrl}>
                {webappUrl}
              </a>
              .
            </p>
          </>
        ),
        keywords: ["register", "sign up", "create account", "google", "apple", "phone", "otp"],
      },
      {
        id: "become-a-rider",
        category: "Getting started",
        question: "How do I become a PABS rider?",
        answerText:
          "Apply at pabs.online/riders with your name, mobile number, and town, and our team will call you. You'll need your own motorcycle with its current OR/CR, a valid driver's license, a valid government ID, and a smartphone. There's nothing to upload on the website; in the rider app you'll upload a valid ID and a profile photo for verification.",
        answer: (
          <>
            <p>
              Fill in the short form at <a className="faq-link" href="/riders">pabs.online/riders</a>:
              just your name, mobile number, and town. Our team will call you to
              set up your account.
            </p>
            <p>You'll need:</p>
            <ul>
              <li>Your own motorcycle, with its current OR/CR</li>
              <li>A valid driver's license</li>
              <li>A valid government-issued ID</li>
              <li>A smartphone (Android or iPhone) with mobile data</li>
            </ul>
            <p>
              There's nothing to upload on the website. Bring these when we
              meet. In the rider app you'll also upload a valid ID and a
              profile photo, and PABS verifies you (usually within 72 hours)
              before you can accept bookings.
            </p>
          </>
        ),
        keywords: ["rider", "apply", "job", "hiring", "driver", "work", "earn", "requirements"],
      },
      {
        id: "list-your-store",
        category: "Getting started",
        question: "How do I list my store on PABS?",
        answerText:
          "Send your details at pabs.online/merchants: your name, business name and type, town, and mobile number. Our team will get in touch to set up your store.",
        answer: (
          <>
            <p>
              Send your details at <a className="faq-link" href="/merchants">pabs.online/merchants</a>:
              your name, business name and type, town, and mobile number. Our
              team will get in touch to confirm your details and set up your
              store.
            </p>
            <p>
              Restaurants and cafés, grocery and sari-sari stores, pharmacies,
              hardware stores, and laundry shops are all welcome.
            </p>
          </>
        ),
        keywords: ["merchant", "store", "restaurant", "list", "sell", "sign up", "partner", "shop"],
      },
      {
        id: "where-to-login",
        category: "Getting started",
        question: "How do I log in?",
        answerText:
          "Open the PABS app and choose Continue with Google, Continue with Apple, Use phone number, or Continue with PIN if you've set a 4-digit PIN. There are no passwords.",
        answer: (
          <>
            <p>
              Open the PABS app (or{" "}
              <a className="faq-link" href={webappUrl}>
                {webappUrl}
              </a>
              ) and choose how to sign in:
            </p>
            <ul>
              <li><strong>Continue with Google</strong> or <strong>Continue with Apple</strong></li>
              <li><strong>Use phone number</strong> — we text you a 6-digit code</li>
              <li><strong>Continue with PIN</strong> — your 4-digit PIN, if you've set one</li>
            </ul>
            <p>There are no passwords on PABS.</p>
          </>
        ),
        keywords: ["login", "log in", "sign in", "pin", "otp", "google", "apple"],
      },
      {
        id: "mobile-app",
        category: "Getting started",
        question: "Do I need the mobile app?",
        answerText:
          "The PABS app is the main way to order, book, and track. You can also use PABS in your browser at https://orders.pabs.online.",
        answer: (
          <>
            <p>
              The PABS app is the main way to order, book, and track, with live
              updates and notifications.{" "}
              <a className="faq-link" href="/download">Get it for iOS or Android</a>.
            </p>
            <p>
              You can also use PABS in your browser at{" "}
              <a className="faq-link" href={webappUrl}>
                {webappUrl}
              </a>
              .
            </p>
          </>
        ),
        keywords: ["mobile", "app", "web", "download"],
      },
      {
        id: "pharmacy-delivery",
        category: "Bookings & services",
        question: "Can I order from a pharmacy?",
        answerText:
          "Yes. Pharmacies are in the Marketplace under Shop more. Pick a pharmacy, add your items, and check out. You can also send a rider on a Pasugo errand to buy something for you.",
        answer: (
          <>
            <p>
              Yes. Pharmacies are in the <strong>Marketplace</strong>, under{" "}
              <strong>Shop more › Pharmacy</strong>. Pick a pharmacy, add your
              items to the cart, and check out.
            </p>
            <p>
              For anything not listed, send a rider on a{" "}
              <a className="faq-link" href="#what-is-pasugo">Pasugo errand</a> to buy it for you.
            </p>
          </>
        ),
        keywords: ["pharmacy", "medicine", "delivery", "essentials"],
      },
      {
        id: "essentials-delivery",
        category: "Bookings & services",
        question: "Can I order groceries and other essentials?",
        answerText:
          "Yes. Tap Pabili to shop groceries and everyday essentials from nearby stores, depending on what's available in your area.",
        answer: (
          <>
            <p>
              Yes. Tap <strong>Pabili</strong> to shop groceries and everyday
              essentials from nearby stores, depending on what's available in
              your area.
            </p>
          </>
        ),
        keywords: ["essentials", "pabili", "grocery", "shopping"],
      },
      {
        id: "food-delivery",
        category: "Bookings & services",
        question: "Can I order food?",
        answerText:
          "Yes. Tap Foods, pick a restaurant or café in the Marketplace, add items to your cart, and check out.",
        answer: (
          <>
            <p>
              Yes. Tap <strong>Foods</strong>, pick a restaurant or café, add
              items to your cart, and check out. See{" "}
              <a className="faq-link" href="#how-to-food-delivery">how to order</a>.
            </p>
          </>
        ),
        keywords: ["food", "foods", "delivery", "drinks", "restaurant"],
      },
      {
        id: "grocery-delivery",
        category: "Bookings & services",
        question: "What's the difference between Pabili and Pasugo?",
        answerText:
          "Pabili is shopping from stores listed in the Marketplace: you pick the store and the items. Pasugo is an errand: you tell a rider what you need, and they can choose the shop for you or bring something you already have.",
        answer: (
          <>
            <p>
              <strong>Pabili</strong> is shopping from stores listed in the
              Marketplace: you pick the store and add the items to your cart.
            </p>
            <p>
              <strong>Pasugo</strong> is an errand: you describe what you need,
              and the rider can pick the shop for you, or pick up something you
              already have and bring it where it needs to go.
            </p>
          </>
        ),
        keywords: ["pabili", "pasugo", "grocery", "difference", "errand"],
      },
      {
        id: "delivery-status",
        category: "Bookings & services",
        question: "How do I track my order or booking?",
        answerText:
          "Open the Bookings tab and tap your booking. You'll see live status updates — Finding a rider, Rider assigned, Rider on the way, Order picked up, On the way to your drop-off, and Order delivered — with the rider on the map.",
        answer: (
          <>
            <p>
              Open the <strong>Bookings</strong> tab and tap your booking. You'll
              see the rider on the map and each status as it happens:
            </p>
            <ul>
              <li>Finding a rider</li>
              <li>Rider assigned</li>
              <li>Rider on the way, then Rider arrived</li>
              <li>Order picked up</li>
              <li>On the way to your drop-off</li>
              <li>Order delivered (or Trip complete for Angkas)</li>
            </ul>
            <p>
              You can chat with or call your rider from the booking, and for
              store orders the receipt photo appears there too.
            </p>
          </>
        ),
        keywords: ["status", "tracking", "updates", "bookings", "map"],
      },
      {
        id: "fares-and-fees",
        category: "Payments & fees",
        question: "How are Pasugo and delivery fares calculated?",
        answerText:
          "Fares use a flag-down rate — ₱50 for Pasugo and deliveries, ₱40 for rides — plus ₱15 per kilometer after the first kilometer, which is free of the per-km rate. A ₱13 per-kilometer launch promo applies for the first two months from each service’s launch. There are no surcharges, night fees, or long-distance add-ons. You’ll always see a fare estimate before you confirm.",
        answer: (
          <>
            <p>
              Pasugo and delivery fares are distance-based and shown as an
              estimate before you confirm the request. The breakdown:
            </p>
            <ul>
              <li><strong>Flag-down rate:</strong> ₱50 for Pasugo and deliveries, ₱40 for Angkas rides.</li>
              <li><strong>Per-kilometer fare:</strong> ₱15 for each kilometer after the first. The first kilometer is free of the per-km rate, so a trip of 1 km or less pays the flag-down rate only.</li>
              <li><strong>Booking fee:</strong> ₱5 per ride, or 3% of the order total capped at ₱30 for deliveries and Pasugo.</li>
            </ul>
            <p>
              As an example, a 4&nbsp;km Pasugo trip costs ₱50 flag-down plus
              ₱45 for the 3&nbsp;km beyond the first — ₱95 in total.
            </p>
            <p>
              <strong>Launch promo:</strong> the per-kilometer rate is ₱13
              instead of ₱15 for the first two months from the launch of each
              service.
            </p>
            <p>
              There are <strong>no surcharges</strong> — no night or hazard
              fees, no long-distance add-ons, and no peak-hour multipliers. The
              rate is the same at 2&nbsp;AM as it is at 2&nbsp;PM.
            </p>
            <p>
              Final totals may adjust based on actual distance traveled or
              changes to the request.
            </p>
          </>
        ),
        keywords: ["fare", "fees", "estimate", "pasugo", "base fare", "per km", "distance"],
      },
      {
        id: "payment-methods",
        category: "Payments & fees",
        question: "What payment methods are supported?",
        answerText:
          "Cash on Delivery or GCash, for every service. With cash, you pay your rider when your order arrives, your errand is done, or your ride ends. With GCash, you send the payment to the PABS GCash account shown in your booking and upload the receipt; PABS confirms it before the rider arrives. PABS never asks for card or bank details.",
        answer: (
          <>
            <p>You choose at checkout, for every service:</p>
            <ul>
              <li>
                <strong>Cash on Delivery</strong> — pay your rider when your
                order arrives, your errand is done, or your ride ends.
              </li>
              <li>
                <strong>GCash</strong> — send the payment to the PABS GCash
                account shown in your booking, then upload a screenshot of the
                receipt. PABS checks it and confirms your payment before the
                rider arrives. For Angkas, the payment is confirmed before a
                rider is assigned.
              </li>
            </ul>
            <p>
              We never ask for card numbers, bank logins, or your GCash PIN.
            </p>
          </>
        ),
        keywords: ["payment", "checkout", "cod", "cash on delivery", "cash", "gcash", "card", "receipt"],
      },
      {
        id: "grocery-payment-preference",
        category: "Payments & fees",
        question: "Do I need to pay upfront for grocery or food orders?",
        answerText:
          "Only if you choose GCash. With Cash on Delivery, the rider pays the store and you settle the total with them when your order arrives. With GCash, you pay PABS in the app and upload the receipt before the rider arrives.",
        answer: (
          <>
            <p>
              <strong>Only if you choose GCash.</strong>
            </p>
            <ul>
              <li>
                <strong>Cash on Delivery:</strong> the rider pays the store and
                keeps the receipt, and you settle the total with them when your
                order arrives.
              </li>
              <li>
                <strong>GCash:</strong> you pay PABS in the app and upload the
                receipt, and it's confirmed before the rider arrives.
              </li>
            </ul>
            <p>
              Store prices can change, so the final amount follows the store
              receipt, which appears in your booking.
            </p>
          </>
        ),
        keywords: ["grocery", "upfront", "payment", "cod", "cash", "gcash"],
      },
      {
        id: "location-data",
        category: "Security & privacy",
        question: "How does PABS use location data?",
        answerText:
          "Your location helps set pickup and drop-off points, work out distance-based fares, and show your rider's position on the map. Riders share their location while they're online, so bookings can be dispatched to the nearest rider and tracked.",
        answer: (
          <>
            <p>
              Your location helps set pickup and drop-off points, work out
              distance-based fares, and show your rider on the map while your
              booking is active.
            </p>
            <p>
              Riders share their location continuously while they're online, so
              bookings go to a nearby rider and customers can follow the trip.
            </p>
          </>
        ),
        keywords: ["location", "gps", "tracking", "map"],
      },
      {
        id: "privacy-and-security",
        category: "Security & privacy",
        question: "Where can I read about privacy and data handling?",
        answerText:
          "See the Privacy Statement for details about what we collect and how we use it.",
        answer: (
          <>
            <p>
              See our{" "}
              <a className="faq-link" href="/privacy">
                Privacy Statement
              </a>{" "}
              for details about what we collect and how we use it.
            </p>
            <p>
              For security practices, see our{" "}
              <a className="faq-link" href="/security">
                Security
              </a>{" "}
              page.
            </p>
          </>
        ),
        keywords: ["privacy", "security"],
      },
      {
        id: "what-we-store",
        category: "Security & privacy",
        question: "What data does PABS store about me?",
        answerText:
          "PABS stores your name, phone number, email, and sign-in accounts (Google or Apple); your addresses and booking history; chat messages with riders and support, including attachments; GCash receipts you upload; photos of delivery receipts; and device details for notifications. Riders also have their ID photos, profile photo, and location while online stored. PABS never stores card numbers, bank logins, or e-wallet PINs.",
        answer: (
          <>
            <p>
              <strong>Stored on our servers:</strong>
            </p>
            <ul>
              <li>Your name, phone number, email, and the Google or Apple account you sign in with.</li>
              <li>Your saved addresses and your booking history.</li>
              <li>Chat messages with riders and support, including photos or files you attach.</li>
              <li>GCash payment receipts you upload, and store receipt photos from your orders.</li>
              <li>Device details and a notification token, so we can send you booking updates.</li>
              <li>
                For riders and merchants: ID photos submitted for verification,
                a profile photo, and, for riders, their location while online.
              </li>
            </ul>
            <p>
              <strong>Never stored:</strong> card numbers, bank logins, or
              e-wallet PINs. See{" "}
              <a className="faq-link" href="#payment-methods">how payment works</a>{" "}
              and the <a className="faq-link" href="/privacy">Privacy Statement</a>.
            </p>
          </>
        ),
        keywords: ["data", "store", "card", "personal", "collect", "information", "id", "receipt"],
      },
      {
        id: "data-protection",
        category: "Security & privacy",
        question: "How is my information protected?",
        answerText:
          "All traffic between the app and our servers is encrypted with HTTPS/TLS. You sign in with Google, Apple, a one-time SMS code, or your PIN, and SMS code requests are rate-limited. Access is role-restricted, and the database sits on a private network with automated backups.",
        answer: (
          <>
            <p>The controls in place today:</p>
            <ul>
              <li><strong>Encrypted in transit:</strong> all traffic between the app and our servers uses HTTPS/TLS.</li>
              <li><strong>Sign-in:</strong> Google, Apple, a one-time SMS code, or your 4-digit PIN. There are no passwords, and SMS code requests are rate-limited to block guessing.</li>
              <li><strong>Who sees what:</strong> customers, riders, merchants, and staff each see only their own scope.</li>
              <li><strong>Database:</strong> runs on a private network that is not reachable from the internet.</li>
              <li><strong>Backups:</strong> automated database backups, with a standby server ready for cutover.</li>
            </ul>
            <p>
              PABS has a designated Data Protection Officer, and our registration
              with the National Privacy Commission is in progress. Data handling
              follows the Data Privacy Act of 2012 (RA 10173) — see our{" "}
              <a className="faq-link" href="/privacy">
                Privacy Statement
              </a>
              .
            </p>
          </>
        ),
        keywords: ["secure", "encryption", "protect", "hacked", "pin", "privacy"],
      },
      {
        id: "is-pabs-registered",
        category: "Security & privacy",
        question: "Is PABS a registered business?",
        answerText:
          "Yes. PABS is registered with the DTI and holds a BIR Certificate of Registration, with Mayor's Permits in Mahinog and Mambajao, Camiguin. It is a locally registered business, not an anonymous app.",
        answer: (
          <>
            <p>
              Yes. PABS is a registered Philippine business, based in Camiguin:
            </p>
            <ul>
              <li>DTI business registration.</li>
              <li>BIR Certificate of Registration.</li>
              <li>Mayor's Permits in Mahinog and Mambajao, Camiguin.</li>
            </ul>
            <p>
              The operator lives on the island, which means there is a named,
              local business accountable to customers, riders, and the local
              government — not a distant platform with no presence here.
            </p>
          </>
        ),
        keywords: ["registered", "legitimate", "dti", "bir", "permit", "legal", "real"],
      },
      {
        id: "rider-accountability",
        category: "Security & privacy",
        question: "Who is my rider?",
        answerText:
          "Riders are verified by PABS before they can accept bookings. Your booking shows your rider's name, photo, rating, and plate number, and records who took it and when each step happened.",
        answer: (
          <>
            <p>
              Riders are verified by PABS before they can accept bookings. Once a
              rider accepts, your booking shows their <strong>name, photo,
              rating, and plate number</strong>, so you know who to expect.
            </p>
            <p>
              Every booking records which rider took it and when each step
              happened, so any problem can be looked into.
            </p>
          </>
        ),
        keywords: ["rider", "verified", "plate", "record", "accountable", "complaint", "safe"],
      },
      {
        id: "delete-account",
        category: "Security & privacy",
        question: "How do I delete my account?",
        answerText:
          "You can request account deletion from inside the app or by contacting support.",
        answer: (
          <>
            <p>You can request account deletion from inside the app.</p>
            <p>
              If you need help, <a href="/contact">contact us</a>.
            </p>
          </>
        ),
        keywords: ["delete", "account"],
      },
      {
        id: "privacy-requests",
        category: "Security & privacy",
        question: "How do I submit a privacy request?",
        answerText:
          "Depending on your location, you may request access, deletion, or export of your information on the Privacy Requests page.",
        answer: (
          <>
            <p>
              Depending on your location, you may request access, deletion, or
              export of your information.
            </p>
            <p>
              See <a href="/privacy-requests">Privacy Requests</a> for details.
            </p>
          </>
        ),
        keywords: ["privacy", "request", "export"],
      },
      {
        id: "support",
        category: "Support",
        question: "How do I contact support?",
        answerText:
          "In the app, go to Profile › Help & Support to open the Help Center and submit a ticket; replies come back in the ticket chat. You can also use the Contact page on this website.",
        answer: (
          <>
            <p>
              In the app, go to <strong>Profile › Help &amp; Support</strong> to
              open the Help Center, choose a topic, and submit a ticket. Replies
              come back in the ticket chat.
            </p>
            <p>
              You can also <a href="/contact">send us a message</a> from this
              website.
            </p>
          </>
        ),
        keywords: ["support", "help", "contact", "ticket"],
      },
      {
        id: "how-to-pharmacy-delivery",
        category: "Bookings & services",
        question: "How do I order from a pharmacy?",
        answerText:
          "Open the Marketplace, go to Shop more › Pharmacy, pick a pharmacy, add your items to the cart, then check out: set Deliver to, choose ASAP or Schedule, pick Cash on Delivery or GCash, and place your order.",
        answer: (
          <>
            <ol>
              <li>Open the <strong>Marketplace</strong> and go to <strong>Shop more › Pharmacy</strong>.</li>
              <li>Pick a pharmacy and add your items to the cart.</li>
              <li>At checkout, set <strong>Deliver to</strong> and choose <strong>ASAP</strong> or <strong>Schedule</strong>.</li>
              <li>Choose <strong>Cash on Delivery</strong> or <strong>GCash</strong>.</li>
              <li>Tap <strong>Place order</strong> and follow it in the Bookings tab.</li>
            </ol>
          </>
        ),
        keywords: ["pharmacy", "medicine", "how to", "steps"],
      },
      {
        id: "how-to-food-delivery",
        category: "Bookings & services",
        question: "How do I order food?",
        answerText:
          "Tap Foods, pick a restaurant or café, add items to your cart (your cart holds one restaurant at a time), then check out: set Deliver to, choose ASAP or Schedule, pick Cash on Delivery or GCash, and tap Place order.",
        answer: (
          <>
            <ol>
              <li>Tap <strong>Foods</strong> and pick a restaurant or café.</li>
              <li>Add items to your cart. Your cart holds one restaurant at a time.</li>
              <li>At checkout, set <strong>Deliver to</strong> and choose <strong>ASAP</strong> or <strong>Schedule</strong>.</li>
              <li>Choose <strong>Cash on Delivery</strong> or <strong>GCash</strong>, and review the total.</li>
              <li>Tap <strong>Place order</strong> and follow it in the Bookings tab.</li>
            </ol>
          </>
        ),
        keywords: ["food", "foods", "order", "restaurant", "how to", "cart"],
      },
      {
        id: "how-to-grocery-delivery",
        category: "Bookings & services",
        question: "How do I shop with Pabili?",
        answerText:
          "Tap Pabili, pick a store, add items to your cart, then check out: set Deliver to, choose ASAP or Schedule, pick Cash on Delivery or GCash, and place your order. Store prices can change, so the final amount follows the store receipt.",
        answer: (
          <>
            <ol>
              <li>Tap <strong>Pabili</strong> and pick a store.</li>
              <li>Add the items you need to your cart.</li>
              <li>At checkout, set <strong>Deliver to</strong> and choose <strong>ASAP</strong> or <strong>Schedule</strong>.</li>
              <li>Choose <strong>Cash on Delivery</strong> or <strong>GCash</strong>.</li>
              <li>Tap <strong>Place order</strong> and follow it in the Bookings tab.</li>
            </ol>
            <p>
              Store prices can change, so the final amount follows the store
              receipt, which appears in your booking.
            </p>
          </>
        ),
        keywords: ["grocery", "pabili", "shopping", "how to", "essentials"],
      },
      {
        id: "what-is-pasugo",
        category: "Bookings & services",
        question: "What is Pasugo?",
        answerText:
          "Pasugo is the PABS errand service. Tell a rider what you need: they can buy it from a shop you choose or one they pick, or pick up something you already have, and bring it to your drop-off.",
        answer: (
          <>
            <p>
              <strong>Pasugo</strong> is the PABS errand service. You tell a
              rider what you need, for example:
            </p>
            <ul>
              <li>Buy something: from a shop you name, or the rider picks one.</li>
              <li>Send something: the rider picks up an item and brings it where it needs to go.</li>
            </ul>
            <p>
              The shop and the drop-off point are both optional. If you leave
              the drop-off out, your rider will ask where to bring it.
            </p>
          </>
        ),
        keywords: ["pasugo", "errand", "buy", "send", "deliver", "pickup"],
      },
      {
        id: "how-to-pasugo",
        category: "Bookings & services",
        question: "How do I send a Pasugo errand?",
        answerText:
          "Tap Pasugo and answer What do you need?. Optionally set Pick up/Buy from (or let the rider pick the shop) and Drop off to. Choose Cash on Delivery or GCash, review, and tap Book now.",
        answer: (
          <>
            <ol>
              <li>Tap <strong>Pasugo</strong> and answer <strong>What do you need?</strong></li>
              <li>
                Optionally set <strong>Pick up/Buy from</strong>. Leave it out
                and the rider picks the shop.
              </li>
              <li>
                Optionally set <strong>Drop off to</strong>. Leave it out and the
                rider will ask where to bring it.
              </li>
              <li>Choose <strong>Cash on Delivery</strong> or <strong>GCash</strong>.</li>
              <li>Review the fare and tap <strong>Book now</strong>.</li>
            </ol>
            <p>
              Without a pickup point, you see the starting fee first, and the
              final fee is set at drop-off.
            </p>
          </>
        ),
        keywords: ["pasugo", "errand", "how to", "send", "book"],
      },
      {
        id: "grocery-food-payment-modes",
        category: "Bookings & services",
        question: "For store orders, who pays the store?",
        answerText:
          "The rider pays the store and keeps the receipt, which appears in your booking. With Cash on Delivery, you pay the rider the total when your order arrives. With GCash, you've already paid PABS in the app.",
        answer: (
          <>
            <p>
              The <strong>rider pays the store</strong> and keeps the receipt,
              and a photo of it appears in your booking.
            </p>
            <ul>
              <li><strong>Cash on Delivery:</strong> you pay the rider the total, items plus fare, when your order arrives.</li>
              <li><strong>GCash:</strong> you've already paid PABS in the app, so there's nothing to hand over.</li>
            </ul>
          </>
        ),
        keywords: ["grocery", "food", "payment", "receipt", "cod", "cash", "gcash"],
      },
      {
        id: "merchant-join",
        category: "For merchants",
        question: "I run a restaurant or store — how do I join PABS?",
        answerText:
          "Sign up as a merchant on PABS to list your store for food, grocery, or pharmacy orders. Start on the free Starter plan or try Business free for 30 days.",
        answer: (
          <>
            <p>
              Merchants can list their restaurant, grocery, or pharmacy on PABS
              and receive orders through Foods and Pabili.
            </p>
            <p>
              Two plans are available:
            </p>
            <ul>
              <li>
                <strong>Starter (Free):</strong> list your store at no cost. A
                small service fee is applied to customer orders so PABS can keep
                the platform running.
              </li>
              <li>
                <strong>Business:</strong> unlocks analytics, Partner and
                Sponsored badges, full order history, marketing tools, and
                removes the customer-facing service fee. Includes a <strong>30-day
                free trial</strong> (one-time per merchant).
              </li>
            </ul>
            <p>
              To get started, <a href="/contact">contact us</a> and we’ll walk
              you through onboarding.
            </p>
          </>
        ),
        keywords: [
          "merchant", "join", "signup", "business", "partner", "restaurant",
          "store", "vendor", "onboarding",
        ],
      },
      {
        id: "merchant-business-plan",
        category: "For merchants",
        question: "What does the Business plan include?",
        answerText:
          "Analytics, Partner + Sponsored badges in search, full order history, marketing tools, and removal of the customer service fee. Billed monthly from your merchant wallet after the 30-day free trial.",
        answer: (
          <>
            <p>The Business plan unlocks:</p>
            <ul>
              <li>
                <strong>Analytics</strong> — daily sales, top items, and peak
                hours so you can see what’s working and fix what’s not.
              </li>
              <li>
                <strong>Partner + Sponsored badges</strong> — your stores get a
                gold star and rank higher in search and on the partner list.
              </li>
              <li>
                <strong>Full order history</strong> — look up every order
                you’ve ever taken, not just the last 7 days.
              </li>
              <li>
                <strong>Marketing tools</strong> — send promos and news to your
                customers (rolling out soon).
              </li>
              <li>
                <strong>No more service fee</strong> — the small fee PABS adds
                on Free orders is removed, so customers see cleaner totals.
              </li>
            </ul>
            <p>
              Business is free during early access and, once paid, is billed
              monthly from your merchant wallet — similar to a subscription.
              The first 30 days are free for every eligible merchant.
            </p>
          </>
        ),
        keywords: [
          "business", "plan", "subscription", "analytics", "badge", "sponsored",
          "marketing", "merchant",
        ],
      },
      {
        id: "merchant-commission",
        category: "For merchants",
        question: "How does PABS earn commission on merchant sales?",
        answerText:
          "You choose how PABS earns on each sale: Add a tip for PABS on top of customer-facing prices, or have it quietly deducted from your merchant wallet after each completed sale.",
        answer: (
          <>
            <p>
              On the Business plan, you can choose how PABS earns on each
              completed sale:
            </p>
            <ul>
              <li>
                <strong>Add a tip for PABS on each sale:</strong> your menu
                shows customers a slightly higher price. You still receive
                exactly the price you set, and PABS keeps the extra amount —
                like a tip at a restaurant.
              </li>
              <li>
                <strong>Take it from my wallet on every sale:</strong> your menu
                shows exactly what you set, with no markup. Every time a sale
                completes, PABS deducts the commission from your merchant
                wallet.
              </li>
            </ul>
            <p>
              Most merchants pick the tip option — customers cover the small
              extra and you don’t have to watch your wallet balance.
            </p>
          </>
        ),
        keywords: [
          "commission", "merchant", "tip", "wallet", "deduct", "topup",
          "auto deduct", "earn",
        ],
      },
      {
        id: "merchant-trial",
        category: "For merchants",
        question: "How does the 30-day Business trial work?",
        answerText:
          "Every eligible merchant gets a one-time 30-day Business trial with all Business features unlocked, no charge. After 30 days, you can continue on Business (billed monthly from your wallet) or return to Starter.",
        answer: (
          <>
            <p>
              Every eligible merchant can start a <strong>one-time 30-day
              Business trial</strong>. During the trial, all Business features
              are unlocked at no charge.
            </p>
            <p>
              After 30 days, you can either continue on Business — billed
              monthly from your merchant wallet — or return to Starter with no
              penalty.
            </p>
          </>
        ),
        keywords: ["trial", "free", "30 days", "merchant", "business"],
      },
      {
        id: "how-to-cancel-order",
        category: "Bookings & services",
        question: "How do I cancel a booking?",
        answerText:
          "Open the Bookings tab, tap the booking, and tap Cancel booking. There's no cancellation fee, but cancellations are capped each week: after the 1st you can't book for 15 minutes, after the 2nd for 1 hour, and after the 3rd for 3 days. A booking can't be cancelled once it's in transit.",
        answer: (
          <>
            <ol>
              <li>Open the <strong>Bookings</strong> tab and tap the booking.</li>
              <li>Tap <strong>Cancel booking</strong> and confirm.</li>
            </ol>
            <p>
              There's <strong>no cancellation fee</strong>, but cancellations are
              capped each week. After your 1st cancellation you can't book for 15
              minutes, after the 2nd for 1 hour, and after the 3rd for 3 days.
            </p>
            <p>
              A booking can't be cancelled once it's in transit. For Angkas,
              cancelling after a rider is assigned may affect your reliability
              score.
            </p>
          </>
        ),
        keywords: ["cancel", "cancellation", "how to", "suspension"],
      },
      {
        id: "how-to-request-refund",
        category: "Payments & fees",
        question: "How do I request a refund?",
        answerText:
          "In the app, go to Profile › Help & Support › Help Center, choose Payments, then Refund Request. Optionally pick the related booking, describe what happened, and tap Submit Ticket. Replies come back in the ticket chat.",
        answer: (
          <>
            <ol>
              <li>Go to <strong>Profile › Help &amp; Support</strong> to open the Help Center.</li>
              <li>Choose <strong>Payments</strong>, then <strong>Refund Request</strong>.</li>
              <li>Pick the <strong>Related Booking</strong> (optional) and describe what happened.</li>
              <li>Tap <strong>Submit Ticket</strong>. Replies come back in the ticket chat.</li>
            </ol>
            <p>Refund outcomes depend on the booking and what happened.</p>
          </>
        ),
        keywords: ["refund", "ticket", "help center", "payments"],
      },
      {
        id: "what-is-angkas",
        category: "Bookings & services",
        question: "What is Angkas?",
        answerText:
          "Angkas is the PABS passenger ride service. A verified rider picks you up and takes you to your destination by motorcycle. The ride is yours alone: your rider takes no other bookings until you arrive. A helmet and hair net are provided free. Pay cash when you arrive, or pay by GCash before a rider is assigned.",
        answer: (
          <>
            <p>
              <strong>Angkas</strong> is the PABS passenger ride service.
              Instead of moving an item, a rider picks you up and takes you to
              your destination by motorcycle.
            </p>
            <ul>
              <li><strong>Your ride is yours alone.</strong> Your rider takes no other bookings until you arrive.</li>
              <li><strong>Helmet and hair net included</strong>, free, on every trip.</li>
              <li><strong>See your rider before they arrive</strong>: name, photo, and rating, with in-app chat or a call if you need to find each other.</li>
              <li><strong>Pay with cash when you arrive, or with GCash</strong>, confirmed before a rider is assigned.</li>
            </ul>
            <p>
              Pickup and destination must be on the same island. See{" "}
              <a className="faq-link" href="#how-to-book-ride">how to book a ride</a>.
            </p>
          </>
        ),
        keywords: [
          "angkas",
          "ride share",
          "ride",
          "passenger",
          "motorcycle",
          "transport",
          "habal",
          "commute",
        ],
      },
      {
        id: "how-to-book-ride",
        category: "Bookings & services",
        question: "How do I book a ride?",
        answerText:
          "Open the PABS app, tap Angkas, and confirm your pickup point. Add your destination to see the fare before you book, or leave it out and pay for the distance you actually travel. Add an optional note or tip, choose Cash or GCash, and tap Book now. You'll see your rider's name, photo, and rating, follow them to your pickup, and pay in cash when you arrive or by GCash before a rider is assigned.",
        answer: (
          <>
            <ol>
              <li>Open the PABS app and tap <strong>Angkas</strong>. You'll need a verified phone number.</li>
              <li>
                Confirm your <strong>pickup point</strong>. It starts at your
                current location; a nearby landmark helps your rider find you.
              </li>
              <li>
                Add your <strong>destination</strong> to see the fare before you
                book. It's optional: leave it out and you pay for the distance
                you actually travel, with the fare so far shown during the trip.
              </li>
              <li>
                Add a note for your rider or an optional tip. Tips go 100% to
                your rider.
              </li>
              <li>Choose <strong>Cash</strong> or <strong>GCash</strong>.</li>
              <li>At <strong>Ready to book?</strong>, review the fare and tap <strong>Book now</strong>.</li>
            </ol>
            <p>
              Once a rider accepts, you'll see their name, photo, and rating, and
              you can follow them to your pickup point and chat or call if
              needed. Pay in cash when you arrive (or by GCash, confirmed before
              a rider is assigned), then rate your rider.
            </p>
          </>
        ),
        keywords: [
          "ride",
          "angkas",
          "ride share",
          "book",
          "pickup",
          "destination",
          "how to",
          "tip",
        ],
      },
      {
        id: "ride-fares",
        category: "Payments & fees",
        question: "How much does a ride cost?",
        answerText:
          "rides use a ₱40 flag-down rate plus ₱15 per kilometer after the first kilometer, which is free of the per-km rate. A ₱5 booking fee applies per ride, and a ₱13 per-kilometer launch promo runs for the first two months.",
        answer: (
          <>
            <p>The published Angkas fare schedule is:</p>
            <ul>
              <li><strong>Flag-down rate:</strong> ₱40 per ride.</li>
              <li><strong>Per-kilometer fare:</strong> ₱15 for each kilometer after the first. A ride of 1 km or less pays the flag-down rate only.</li>
              <li><strong>Booking fee:</strong> ₱5 per ride, paid by the passenger on top of the fare.</li>
            </ul>
            <p>Some worked examples, booking fee included:</p>
            <ul>
              <li>A <strong>1&nbsp;km</strong> hop — ₱40 flag-down only, so ₱45 all-in.</li>
              <li>A <strong>3.5&nbsp;km</strong> trip — ₱40 plus ₱37.50 for the 2.5&nbsp;km beyond the first, so ₱82.50 all-in.</li>
              <li>A <strong>6&nbsp;km</strong> cross-town ride — ₱40 plus ₱75 for the 5&nbsp;km beyond the first, so ₱120 all-in.</li>
            </ul>
            <p>
              For the first two months after launch, the per-kilometer
              rate is ₱13 instead of ₱15, which makes those trips cheaper still.
            </p>
            <p>
              The fare is the same for everyone — residents and visitors alike.
              There is no haggling and no negotiating at the roadside.
            </p>
            <p>
              Add your destination when you book to see the fare before you
              confirm; without one, you pay for the distance you actually
              travel.
            </p>
          </>
        ),
        keywords: [
          "pasakay",
          "ride",
          "fare",
          "cost",
          "price",
          "flag-down",
          "per km",
        ],
      },
      {
        id: "ride-safety",
        category: "Bookings & services",
        question: "What are the safety rules for rides?",
        answerText:
          "Only riders cleared to carry passengers can accept rides. Every ride includes a free helmet and hair net; always wear the helmet. One passenger per ride, and a child may come along only if you can hold them the whole way. Tell your rider if you're carrying bags, and check the plate number before you get on.",
        answer: (
          <>
            <p>
              Only riders who have been cleared to carry passengers can accept
              ride bookings. On every trip:
            </p>
            <ul>
              <li>Every ride includes a <strong>free helmet and hair net</strong>. Always wear the helmet your rider gives you.</li>
              <li><strong>One passenger per ride.</strong> A child may come along only if you can hold them the whole way.</li>
              <li><strong>Carrying bags?</strong> Mention it in your note so your rider knows before pickup.</li>
              <li><strong>Check the plate number</strong> before you get on.</li>
            </ul>
          </>
        ),
        keywords: [
          "ride",
          "safety",
          "helmet",
          "hair net",
          "passenger",
          "child",
          "bags",
          "rules",
        ],
      },
      {
        id: "customer-booking-fee",
        category: "Payments & fees",
        question: "What is the booking fee?",
        answerText:
          "A platform service fee paid by the customer on top of the fare: ₱5 per ride, or 3% of the order total capped at ₱30 for deliveries and Pasugo. It does not reduce the rider's earnings.",
        answer: (
          <>
            <p>
              The booking fee is a platform service fee added on top of the
              fare:
            </p>
            <ul>
              <li><strong>rides:</strong> ₱5 per booking.</li>
              <li><strong>Deliveries and Pasugo:</strong> 3% of the order total, capped at ₱30 per booking.</li>
            </ul>
            <p>
              The fee goes to PABS and covers platform costs — servers,
              development, and support. It{" "}
              <strong>does not reduce what the rider earns</strong>: riders keep
              the full fare they collect, and PABS charges them no commission on
              it.
            </p>
          </>
        ),
        keywords: [
          "booking fee",
          "service fee",
          "platform fee",
          "commission",
          "3%",
        ],
      },
];
